import { styled, LazyImage } from '@teko/ds-desktop';
import { CloseOutlined, GreenGiffColored } from '@teko/ds-icon';
import { BottomSheet } from '@teko/ds-mobile';
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../../i18n.es.js';
import { SGC_COLORS } from './constants.es.js';
import { useDevice } from '../../contexts/DeviceContext.es.js';

const TOOLTIP_WIDTH = 270;
const POPUP_GAP = 8;
const GiftRow = ({ gift }) => /* @__PURE__ */ React.createElement(GiftListItem, null, /* @__PURE__ */ React.createElement(GiftThumb, null, gift.src ? /* @__PURE__ */ React.createElement(LazyImage, {
  src: gift.src,
  width: 32,
  height: 32,
  imageWidth: 64,
  alt: gift.alt,
  contain: true,
  aspectRatio: 1,
  draggable: false
}) : /* @__PURE__ */ React.createElement(GreenGiffColored, {
  size: 20
})), /* @__PURE__ */ React.createElement("span", null, gift.alt, " ", /* @__PURE__ */ React.createElement("strong", null, "x", gift.quantity)));
const ProductGiftsSgc = ({
  gifts,
  hasGift
}) => {
  var _a;
  const { isMobile } = useDevice();
  const [showSheet, setShowSheet] = useState(false);
  const [tip, setTip] = useState();
  const blockRef = useRef(null);
  if (!hasGift || !((_a = gifts == null ? void 0 : gifts.images) == null ? void 0 : _a.length))
    return null;
  const [first, ...rest] = gifts.images;
  const moreCount = rest.length;
  const hasMore = moreCount > 0;
  const openTip = () => {
    var _a2;
    const anchor = (_a2 = blockRef.current) == null ? void 0 : _a2.getBoundingClientRect();
    if (!anchor)
      return;
    setTip({
      left: Math.max(POPUP_GAP, anchor.right - TOOLTIP_WIDTH),
      bottom: window.innerHeight - anchor.top + POPUP_GAP,
      width: TOOLTIP_WIDTH
    });
  };
  const giftList = /* @__PURE__ */ React.createElement(GiftList, null, gifts.images.map((gift, index) => /* @__PURE__ */ React.createElement(GiftRow, {
    key: index,
    gift
  })));
  const moreBox = /* @__PURE__ */ React.createElement(MoreBox, null, "+", moreCount, " ", t("qu\xE0"));
  return /* @__PURE__ */ React.createElement(Wrapper, null, /* @__PURE__ */ React.createElement(Block, {
    ref: blockRef,
    hasMore,
    onMouseEnter: () => {
      if (!isMobile)
        openTip();
    },
    onMouseLeave: () => setTip(void 0),
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      if (isMobile)
        setShowSheet(true);
    }
  }, /* @__PURE__ */ React.createElement(GiftText, null, t("T\u1EB7ng"), ": ", first.alt), hasMore && moreBox), !isMobile && tip && typeof document !== "undefined" ? createPortal(/* @__PURE__ */ React.createElement(PortalTip, {
    style: { left: tip.left, bottom: tip.bottom, width: tip.width }
  }, giftList), document.body) : null, isMobile && typeof document !== "undefined" ? createPortal(/* @__PURE__ */ React.createElement(FullWidthBottomSheet, {
    show: showSheet,
    contentHeight: "300px",
    backdropClicked: () => setShowSheet(false)
  }, /* @__PURE__ */ React.createElement(SheetContent, null, /* @__PURE__ */ React.createElement(SheetHeader, null, /* @__PURE__ */ React.createElement(SheetTitle, null, t("Qu\xE0 t\u1EB7ng")), /* @__PURE__ */ React.createElement(CloseOutlined, {
    size: 24,
    onClick: () => setShowSheet(false)
  })), giftList)), document.body) : null);
};
const Wrapper = styled.div`
  width: 100%;
`;
const Block = styled.div(({ hasMore }) => `
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  ${hasMore ? "cursor: pointer;" : ""}
`);
const giftBoxStyles = (theme) => `
  background: ${SGC_COLORS.tooltipBackground};
  padding: ${theme == null ? void 0 : theme.spacing.s4};
  border-radius: ${theme == null ? void 0 : theme.borderRadius.s};
  color: ${theme == null ? void 0 : theme.colors.textTitle};
  font-size: 12px;
  line-height: 16px;
`;
const GiftText = styled.span(({ theme }) => `
  ${giftBoxStyles(theme)}
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`);
const MoreBox = styled.span(({ theme }) => `
  ${giftBoxStyles(theme)}
  flex: 0 0 auto;
  white-space: nowrap;
`);
const GiftList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;
const PortalTip = styled.div(({ theme }) => `
  position: fixed;
  z-index: 1070;
  padding: ${theme == null ? void 0 : theme.spacing.s12};
  border-radius: ${theme == null ? void 0 : theme.borderRadius.s};
  background: ${SGC_COLORS.tooltipBackground};
  color: ${theme == null ? void 0 : theme.colors.textTitle};
  box-shadow: -1px 2px 0 0 ${theme == null ? void 0 : theme.colors.border};

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    right: 20px;
    width: 10px;
    height: 10px;
    background: ${SGC_COLORS.tooltipBackground};
    border-bottom: 1px solid ${theme == null ? void 0 : theme.colors.border};
    transform: rotate(45deg);
    box-shadow: 0 1px 0 0 ${theme == null ? void 0 : theme.colors.border};
  }
`);
const GiftListItem = styled.div(({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme == null ? void 0 : theme.spacing.s8};
  font-size: 12px;
  line-height: 16px;
  color: ${theme == null ? void 0 : theme.colors.textTitle};
`);
const GiftThumb = styled.div(({ theme }) => `
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: ${theme == null ? void 0 : theme.borderRadius.s};
  overflow: hidden;
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
`);
const SheetContent = styled.div(({ theme }) => `
  padding: ${theme == null ? void 0 : theme.spacing.s16};
`);
const FullWidthBottomSheet = styled(BottomSheet)`
  left: 0;
  right: 0;
  width: 100%;
`;
const SheetHeader = styled.div(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme == null ? void 0 : theme.spacing.s12};
`);
const SheetTitle = styled.span(({ theme }) => `
  font-size: 16px;
  font-weight: 500;
  color: ${theme == null ? void 0 : theme.colors.textTitle};
`);

export { ProductGiftsSgc, ProductGiftsSgc as default };
